import React, { useEffect, useRef, useState } from "react";
import "./Pages.css";

export default function Partnership() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const widgetRef = useRef(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);

  useEffect(() => {
    if (!siteKey) {
      console.warn(
        "Turnstile site key is not configured for this environment.",
      );
      return;
    }

    const scriptId = "cf-turnstile-script";
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => setTurnstileLoaded(true);
      script.onerror = () => {
        console.error("Failed to load Cloudflare Turnstile script.");
      };
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }

    if (window.turnstile) {
      setTurnstileLoaded(true);
    }

    return undefined;
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey || !turnstileLoaded || !widgetRef.current || !window.turnstile)
      return;
    if (typeof window.turnstile.render !== "function") return;

    if (widgetRef.current.dataset.turnstileRendered === "true") {
      return;
    }

    try {
      const widgetId = window.turnstile.render(widgetRef.current, {
        sitekey: siteKey,
        theme: "light",
        appearance: "always",
      });

      widgetRef.current.dataset.widgetId = widgetId;
      widgetRef.current.dataset.turnstileRendered = "true";
    } catch (error) {
      console.error("Turnstile render failed:", error);
    }
  }, [siteKey, turnstileLoaded]);

  const resetTurnstile = () => {
    if (
      window.turnstile &&
      widgetRef.current &&
      widgetRef.current.dataset.widgetId
    ) {
      window.turnstile.reset(widgetRef.current.dataset.widgetId);
    }
  };

  const showModal = () => {
    const modal = document.getElementById("thankYouModal");
    if (modal) modal.style.display = "flex";
  };

  const closeModal = () => {
    const modal = document.getElementById("thankYouModal");
    if (modal) modal.style.display = "none";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!siteKey) {
      alert(
        "Turnstile is not configured for this environment. Add REACT_APP_TURNSTILE_SITE_KEY to your local .env file.",
      );
      return;
    }

    const token =
      window.turnstile && widgetRef.current
        ? window.turnstile.getResponse(widgetRef.current.dataset.widgetId)
        : "";

    if (!token) {
      alert(
        "Please complete the security check before submitting your request.",
      );
      return;
    }

    const formData = {
      fullName: e.target.fullName.value.trim(),
      email: e.target.email.value.trim(),
      phone: e.target.phone.value.trim(),
      organization: e.target.organization.value.trim(),
      industry: e.target.industry.value,
      address: e.target.address.value.trim(),
      partnershipType: e.target.partnershipType.value,
      message: e.target.message.value.trim(),
      turnstileToken: token,
    };

    let errors = [];
    if (!formData.fullName) errors.push("Full Name is required.");
    if (!formData.email) errors.push("Email is required.");
    else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.push("Please enter a valid email address.");
      }
    }

    if (errors.length > 0) {
      alert(errors.join("\n"));
      resetTurnstile();
      return;
    }

    try {
      const response = await fetch(`${baseUrl}/api/partnership`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        showModal();
        e.target.reset();
        resetTurnstile();
      } else {
        alert(data.message || "Submission failed. Please try again.");
        resetTurnstile();
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("An error occurred. Please try again.");
      resetTurnstile();
    }
  };

  return (
    <section className="partnership-section">
      <div className="form-container">
        <h1>Partner with AKI Battery Recycling</h1>
        <p>
          We're always open to collaborating with organizations that share our
          commitment to sustainability.
        </p>

        <form onSubmit={handleSubmit} className="partnership-form">
          <div className="form-group">
            <label>Full Name*</label>
            <input type="text" name="fullName" required />
          </div>

          <div className="form-group">
            <label>Email*</label>
            <input type="email" name="email" required />
          </div>

          <div className="form-group">
            <label>Phone</label>
            <input type="text" name="phone" />
          </div>

          <div className="form-group">
            <label>Organization Name</label>
            <input type="text" name="organization" />
          </div>

          <div className="form-group">
            <label>Industry</label>
            <select name="industry">
              <option>Battery Manufacturer</option>
              <option>Automotive OEM</option>
              <option>Vehicle Dismantler / Recycler</option>
              <option>Automotive Dealer / Service Centre</option>
              <option>Retailer / Electronics Store</option>
              <option>Municipality / Public Works</option>
              <option>School / Academic Institution</option>
              <option>Logistics or Waste Transport Provider</option>
              <option>Recycling Company / Processor</option>
              <option>Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Location</label>
            <input type="text" name="address" />
          </div>

          <div className="form-group">
            <label>Partnership Type</label>
            <select name="partnershipType">
              <option>Recycling Collaboration</option>
              <option>Battery Collection Site</option>
              <option>E-Waste Drop-Off</option>
              <option>Educational Outreach</option>
              <option>Other</option>
            </select>
          </div>

          <div className="form-group">
            <label>Message</label>
            <textarea name="message" rows="4" />
          </div>

          <div
            ref={widgetRef}
            className="turnstile-wrap"
            style={{ marginBottom: "16px" }}
          />

          <button type="submit">Submit Request</button>
        </form>
      </div>

      {/* Custom Modal */}
      <div
        className="modal-overlay"
        id="thankYouModal"
        style={{ display: "none" }}
        onClick={(e) => {
          if (e.target.id === "thankYouModal") closeModal();
        }}
      >
        <div className="modal-box">
          <button className="modal-close" onClick={closeModal}>
            &times;
          </button>
          <h2>Thank You!</h2>
          <div className="modal-content">
            <p>
              We appreciate your interest in partnering with AKI Battery
              Recycling. We'll reach out to you shortly.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
